import React, { useCallback, useState } from 'react'
import {
  FormControl,
  FormHelperText,
  TextField,
  Collapse,
} from '@mui/material'
import { secondaryText, primary } from '@/constant'
import { WebhookProps } from '@/types'
import { GrayCard, Row } from './components'
import { Delete } from '@/icon'
import { Alert } from '@mui/material'
import Text from '@/components/Text'
import Button from '@/components/Button'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { MessageChannelTypes } from '@/types'
import Test from './testChannel'
import InfoIcon from '@mui/icons-material/Info'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'

export interface WebhookConfigProps {
  id?: string;
  webhookType?: MessageChannelTypes;
  name: string;
  helperText: string;
  helperLink: string;
  value: WebhookProps[];
  onChange: (value: WebhookProps[]) => void;
  validate: boolean;
  expand: boolean;
  onExpand: (expand: boolean) => void;
  hideSignInput?: boolean;
}

const WebhookConfig: React.FC<WebhookConfigProps> = (props) => {
  const {
    id,
    webhookType,
    name,
    value,
    onChange,
    helperText,
    helperLink,
    validate,
    expand,
    onExpand,
    hideSignInput,
  } = props
  const [errors, setErrors] = useState<string[]>()

  const handleAdd = useCallback(() => {
    const items = [
      ...(value ?? []),
      { id: `webhook-${value?.length}`, address: '', sign: '' },
    ]
    onChange(items)
  }, [onChange, value])

  const handleRemove = useCallback(
    (itemId: string) => {
      const items = [...(value ?? [])]
      items.splice(
        value?.findIndex((item) => item?.id === itemId),
        1
      )
      onChange(items)
    },
    [onChange, value]
  )

  const handleChangeItem = useCallback(
    (itemValue: WebhookProps) => {
      const items = [...(value ?? [])]
      // @ts-ignore
      items.find((item) => item?.id === itemValue?.id).address =
        itemValue?.address
      // @ts-ignore
      items.find((item) => item?.id === itemValue?.id).sign = itemValue?.sign
      onChange(items)
    },
    [onChange, value]
  )

  const handleClick = useCallback(() => {
    onExpand(!expand)
  }, [expand, onExpand])

  return (
    <GrayCard id={id} expand={expand ? 'true' : 'false'}>
      <Text
        weight='bold'
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={handleClick}
      >
        {name}机器人
        {expand ? (
          <RemoveCircleOutlineIcon
            sx={{ fontSize: '14px', color: secondaryText }}
          />
        ) : (
          <AddCircleOutlineIcon
            sx={{ fontSize: '14px', color: secondaryText }}
          />
        )}
      </Text>
      <Collapse in={expand} sx={{ padding: 0 }}>
        <Alert
          severity='info'
          sx={{ alignItems: 'center' }}
          icon={
            <InfoIcon
              sx={{
                color: '#2f7ce9',
                fontSize: '14px',
              }}
            />
          }
        >
          <Text
            size='xs'
            sx={{ color: secondaryText, display: 'flex', alignItems: 'center' }}
          >
            {helperText}
            <a
              style={{ color: primary }}
              rel='noreferrer'
              href={helperLink}
              target={'_blank'}
            >
              查看如何获取 webhook 信息
            </a>
          </Text>
        </Alert>
        <FormControl sx={{ width: '100%', marginTop: '16px' }}>
          {value?.map((item) => (
            <WebhookItem
              key={item?.id}
              id={item?.id}
              address={item?.address}
              sign={item?.sign}
              validate={validate}
              error={errors?.includes(item?.address)}
              onChange={handleChangeItem}
              onDelete={handleRemove}
              hideSignInput={hideSignInput}
            />
          ))}
          <Row sx={{ gap: '8px' }}>
            {value?.length < 5 && (
              <Button
                type={'text'}
                onClick={handleAdd}
                startIcon={<AddCircleOutlineIcon fontSize='small' />}
                sx={{
                  padding: '0',
                  backgroundColor: 'transparent !important',
                  minWidth: 'auto !important',
                }}
              >
                添加
              </Button>
            )}
            <Test
              // @ts-ignore
              type={webhookType}
              contents={
                value?.map((item) => ({
                  address: item?.address,
                  sign: item?.sign,
                })) ?? []
              }
              onTestEnd={setErrors}
            />
          </Row>
        </FormControl>
      </Collapse>
    </GrayCard>
  )
}

const WebhookItem = (
  props: WebhookProps & {
    error?: boolean;
    validate: boolean;
    onChange: (value: WebhookProps) => void;
    onDelete: (itemId: string) => void;
    hideSignInput?: boolean;
  }
) => {
  const {
    id,
    address,
    sign,
    error,
    onChange,
    onDelete,
    validate,
    hideSignInput,
  } = props

  const handleChangeAddress = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event?.target?.value
      onChange({ id, sign, address: value?.trim() })
    },
    [id, onChange, sign]
  )

  const handleChangeSign = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event?.target?.value
      onChange({ id, address, sign: value?.trim() })
    },
    [address, id, onChange]
  )

  const handleDelete = useCallback(
    (id: string) => () => {
      onDelete(id)
    },
    [onDelete]
  )

  return (
    <Row sx={{ marginBottom: '8px', alignItems: 'flex-end', gap: '8px' }}>
      <FormControl sx={{ width: '100%' }}>
        <TextField
          key='webhook-address'
          fullWidth={true}
          label={'机器人webhook地址'}
          required={true}
          error={error}
          value={address}
          onChange={handleChangeAddress}
        />
        {error && !validate && (
          <FormHelperText sx={{ marginLeft: 0 }} error={true}>
            配置错误
          </FormHelperText>
        )}
        {validate && address.length === 0 && (
          <FormHelperText sx={{ marginLeft: 0 }} error={true}>
            请输入webhook地址
          </FormHelperText>
        )}
      </FormControl>
      {!hideSignInput && (
        <FormControl sx={{ width: '100%' }}>
          <TextField
            key='webhook-sign'
            fullWidth={true}
            label={'密钥'}
            value={sign}
            onChange={handleChangeSign}
          />
          {error && !validate && (
            <FormHelperText sx={{ marginLeft: 0 }} error={true}>
              {' '}
            </FormHelperText>
          )}
          {validate && address.length === 0 && (
            <FormHelperText sx={{ marginLeft: 0 }} error={true}>
              {' '}
            </FormHelperText>
          )}
        </FormControl>
      )}
      {/*  @ts-ignore */}
      <Delete sx={{ color: '#666666' }} onClick={handleDelete(id)} />
    </Row>
  )
}

export default WebhookConfig
